import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import { AriaAlertContext } from '../../AriaAlertContext';
import { colors } from '../constants';
import { cloneField, getFieldClone } from '../utilities/syntheticEventTracking';

import {
  ErrorMessageBubble,
  ErrorMessageBubbleIcon,
  ErrorMessageText,
} from './MessageBubble';
import { MultiSelectInputOption } from './MultiSelectInputOption';
import SubHeading from './SubHeading';

/* MultiSelectInput */

// Input allows selection of multiple values
// Values are stored in one array that is passed to the form

interface StyledValidationProps {
  isVisible?: boolean;
}

interface StyledHeadingProps {
  centerHeading?: boolean;
}

interface MultiSelectInputProps {
  name: string;
  value: Array<string>;
  heading: string;
  options: Array<{ label: string; enum: string }>;
  disabled?: boolean;
  error?: any;
  touched?: any;
  ariaLabelledBy?: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue:
    | ((name: string, value: Array<string>) => void)
    | (() => undefined);
  centerHeading?: boolean;
}

const Container = styled.div`
  margin-bottom: 0.25rem;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
`;
Container.displayName = 'Container';

const FormSectionSubHeading = styled(SubHeading)<StyledHeadingProps>`
  width: 100%;
  font-size: 0.875rem;
  line-height: 1.125rem;
  text-align: ${(p: StyledHeadingProps) =>
    p.centerHeading ? 'center' : 'left'};
  color: ${colors.TEXT_PRIMARY};
`;
FormSectionSubHeading.displayName = 'FormSectionSubHeading';

const OptionsContainer = styled.ul`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
`;
OptionsContainer.displayName = 'OptionsContainer';

const HiddenLabel = styled.label`
  display: none;
`;
HiddenLabel.displayName = 'HiddenLabel';

const HiddenInput = styled.input`
  display: none;
`;
HiddenInput.displayName = 'HiddenInput';

// Hidden error message is revealed when an error exists for a touched input
// Props:
// visible - toggles display between flex and none
const ErrorMessage = styled(ErrorMessageBubble)<StyledValidationProps>`
  display: ${(p: StyledValidationProps) => (p.isVisible ? 'flex' : 'none')};
  &:last-child {
    min-width: 100%;
  }
`;
ErrorMessage.displayName = 'ErrorMessage';

export const MultiSelectInput: FC<MultiSelectInputProps> = ({
  name, // used to set id attribute for proper form functionality
  value,
  heading,
  options,
  disabled,
  error,
  touched,
  ariaLabelledBy,
  onChange,
  setFieldValue,
  centerHeading,
}) => {
  const { setAriaAlertMessage } = useContext(AriaAlertContext);

  // Toggle option
  const onClickOption = (
    clickedOption: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    // Cache the field value for use in synthetic event creation
    let selection;
    // Remove option if it is already in value
    if (value.includes(clickedOption)) {
      const current = value.filter(
        (previousValue) => previousValue !== clickedOption
      );
      setFieldValue(name, current);
      selection = current.toString();
    } else {
      // Add option if it was not already in value
      value.push(clickedOption);
      setFieldValue(name, value);
      selection = value.toString();
    }
    // Trigger synthetic change for Heap tracking purposes
    trackChangeOnClickOption(selection, e);
  };

  // Catch-all workaround to inconsistent browser event tracking
  function trackChangeOnClickOption(
    selection: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) {
    try {
      // Cast the blur event clone as a change event
      const syntheticEvent = new Event('change', e.nativeEvent);
      // Get the form of the input field
      const { form } = e.target as HTMLInputElement;
      // Get the corresponding element clone if one exists, otherwise create one
      let clone = getFieldClone(name);
      const firstChange = !clone && selection;
      if (!clone) {
        // Get the corresponding input field element to be cloned
        const fieldNodes = document.getElementsByName(name);
        const field = fieldNodes.length === 1 ? fieldNodes[0] : null;
        if (!field) return;
        clone = cloneField(name, field, form);
      }
      // Dispatch the synthetic event to simulate the change when the clone/real values differ
      const cloneValue = clone.getAttribute('value');
      if (cloneValue !== selection || firstChange) {
        clone.dispatchEvent(syntheticEvent);
        clone.setAttribute('value', selection);
      }
    } catch {
      // TO-DO error handler - probably not worth a NPC-Client-Error
    }
  }

  const handleBlur = () => {
    if (error && setAriaAlertMessage) {
      setAriaAlertMessage(error);
    }
  };

  return (
    <Container>
      <FormSectionSubHeading centerHeading={centerHeading}>
        {heading}
      </FormSectionSubHeading>
      <OptionsContainer
        id={name}
        role="listbox"
        aria-multiselectable="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={`${name}-error`}
        aria-invalid={error && touched}
        onBlur={handleBlur}
        tabIndex={-1}
      >
        {options.map((option, index) => (
          <MultiSelectInputOption
            key={`${name}-option-${option.enum.toString()}`}
            id={`${name}-option-${index.toString()}`}
            label={option.label}
            selected={value.includes(option.enum)}
            disabled={disabled || false}
            onClick={(e) => onClickOption(option.enum, e)}
          />
        ))}
      </OptionsContainer>
      <HiddenLabel htmlFor={name}>
        {name}
        <HiddenInput
          type="hidden"
          name={name}
          value={value}
          onChange={(e) => onChange(e)}
        />
      </HiddenLabel>
      <ErrorMessage isVisible={error && touched}>
        <ErrorMessageText id={`${name}-error`}>{error}</ErrorMessageText>
        <ErrorMessageBubbleIcon />
      </ErrorMessage>
    </Container>
  );
};

MultiSelectInput.defaultProps = {
  disabled: false,
  error: undefined,
  touched: undefined,
  ariaLabelledBy: undefined,
  centerHeading: false,
};
